import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useForm } from '../hooks/useForm';
import { analyzeCase, ApiError, getAppointments, getFormSchema, saveFormResponse, getTreatmentPackages, updateUserHistory, type Appointment, type TreatmentPackage, type FormLanguage } from '../services/api';
import { AnalysisResponse, FormSchemaResponse, FormData } from '../types';
import { useAuth } from '../contexts/AuthContext';
import ProgressHeader from './ProgressHeader';
import ErrorBanner from './ErrorBanner';
import FormSection from './FormSection';
import TesterNameField from './TesterNameField';
import SelectField from './SelectField';
import DynamicFormField from './DynamicFormField';
import './DiscoveryCallForm.css';

const DiscoveryCallForm: React.FC = () => {
  const { email } = useAuth();
  const {
    formData,
    errors,
    session,
    isSubmitting,
    setIsSubmitting,
    updateField,
    validate,
    validateField,
  } = useForm();

  // Set tester name to email when component mounts or email changes
  useEffect(() => {
    if (email) {
      updateField('tester_name', email);
    }
  }, [email, updateField]);

  const [currentSection, setCurrentSection] = useState(1);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set([1])
  );
  const [completedSections] = useState<Set<number>>(
    new Set()
  );
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [testerNameError, setTesterNameError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResponse | null>(
    null
  );
  const [showOutput, setShowOutput] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | ''>('');
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<number | null>(null);
  const [isAppointmentApplied, setIsAppointmentApplied] = useState(false);
  const isFormEnabled = isAppointmentApplied; // Form is enabled only after Apply is clicked
  const [formSchema, setFormSchema] = useState<FormSchemaResponse | null>(null);
  const [isLoadingSchema, setIsLoadingSchema] = useState(true);
  const [language, setLanguage] = useState<FormLanguage>('english');
  const [showStartSessionModal, setShowStartSessionModal] = useState(false);
  const [packages, setPackages] = useState<TreatmentPackage[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | ''>('');
  const [packageError, setPackageError] = useState<string | null>(null);
  const [sessionForm, setSessionForm] = useState({
    treatmentRecommendation: '',
    sexologistType: '',
    reasonNotStarted: '',
    leadStatus: '',
    discountOffered: '',
    triggerOfTreatment: '',
    comments: '',
    lockIn: 'No',
    followUpDate: '',
    followUpTime: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const testerNameRef = useRef<HTMLInputElement>(null);

  // Fetch form schema when component mounts or language changes
  useEffect(() => {
    const fetchFormSchema = async () => {
      setIsLoadingSchema(true);
      try {
        const response = await getFormSchema(language);
        if (response.statusCode === 200 && response.data) {
          setFormSchema(response);
        }
      } catch (error) {
        console.error('Failed to fetch form schema:', error);
        alert('Failed to load form. Please refresh the page.');
      } finally {
        setIsLoadingSchema(false);
      }
    };

    fetchFormSchema();
  }, [language]);

  // Fetch appointments when component mounts
  useEffect(() => {
    const fetchAppointments = async () => {
      setIsLoadingAppointments(true);
      try {
        const response = await getAppointments();
        if (response.statusCode === 200 && response.data) {
          setAppointments(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch appointments:', error);
        // Don't show error to user, just log it
      } finally {
        setIsLoadingAppointments(false);
      }
    };

    fetchAppointments();
  }, []);

  const fetchPackages = useCallback(async () => {
    setIsLoadingPackages(true);
    setPackageError(null);
    try {
      const response = await getTreatmentPackages();
      setPackages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch treatment packages:', error);
      setPackageError('Failed to load treatment packages');
    } finally {
      setIsLoadingPackages(false);
    }
  }, []);

  // Apply appointment and enable form
  const handleApplyAppointment = useCallback(() => {
    if (selectedPatientId === '' || selectedPatientId === null || selectedPatientId === undefined) {
      return;
    }
    setIsAppointmentApplied(true);
  }, [selectedPatientId]);

  useEffect(() => {
    if (showStartSessionModal && packages.length === 0 && !isLoadingPackages) {
      fetchPackages();
    }
  }, [showStartSessionModal, packages.length, isLoadingPackages, fetchPackages]);

  // Update current section when user interacts with fields
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const section = target.closest('[data-section]') as HTMLElement;
      if (section) {
        const sectionNum = parseInt(section.dataset.section || '0');
        if (sectionNum && sectionNum !== currentSection) {
          setCurrentSection(sectionNum);
          setExpandedSections((prev) => new Set([...prev, sectionNum]));
        }
      }
    };

    document.addEventListener('focus', handleFocus, true);
    return () => document.removeEventListener('focus', handleFocus, true);
  }, [currentSection]);

  const formatPackageLabel = useCallback((pkg: TreatmentPackage) => {
    const counsellor = pkg.counsellor_consultations ?? 0;
    const sexologist = pkg.sexologist_consultations ?? 0;
    const therapist = pkg.therapist_consultations ?? 0;
    return `${pkg.name} (\u20B9${pkg.price}) - C(${counsellor}) - S(${sexologist}) - T(${therapist})`;
  }, []);

  const updateSessionField = useCallback((field: keyof typeof sessionForm, value: string) => {
    setSessionForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleStartSessionSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      
      // Validate user_id is available
      if (selectedPatientId === '' || selectedPatientId === null || selectedPatientId === undefined) {
        alert('Please select a patient first');
        return;
      }

      // Build JSON body with all fields
      const payload = {
        user_id: typeof selectedPatientId === 'number' ? selectedPatientId : parseInt(String(selectedPatientId)),
        appointment_id: selectedAppointmentId || undefined,
        treatment_package: selectedPackageId !== '' ? (typeof selectedPackageId === 'number' ? selectedPackageId : parseInt(String(selectedPackageId))) : null,
        treatment_recommendation: sessionForm.treatmentRecommendation || null,
        type_of_sexologist_recommended: sessionForm.sexologistType || null,
        reason_of_treatment_not_started: sessionForm.reasonNotStarted || null,
        status_of_lead: sessionForm.leadStatus || null,
        discount_offered: sessionForm.discountOffered || null,
        trigger_of_treatment: sessionForm.triggerOfTreatment || null,
        comments: sessionForm.comments || null,
        lock_in: sessionForm.lockIn || null,
        follow_up_date: sessionForm.followUpDate || null,
        follow_up_time: sessionForm.followUpTime || null,
      };

      // Print formatted JSON body
      console.log('Start New Session - JSON Body:');
      console.log(JSON.stringify(payload, null, 2));
      
      // Also print in a readable format
      console.log('\nStart New Session - Field Values:');
      Object.entries(payload).forEach(([field, value]) => {
        console.log(`  ${field}: ${value === null || value === undefined ? '(empty)' : value}`);
      });

      // Call API to update user history
      try {
        const response = await updateUserHistory(payload);
        console.log('User history updated successfully:', response);
        alert('User history updated successfully!');
        setShowStartSessionModal(false);
        
        // Reset form after successful submission
        setSessionForm({
          treatmentRecommendation: '',
          sexologistType: '',
          reasonNotStarted: '',
          leadStatus: '',
          discountOffered: '',
          triggerOfTreatment: '',
          comments: '',
          lockIn: 'No',
          followUpDate: '',
          followUpTime: '',
        });
        setSelectedPackageId('');
        setUploadFile(null);
      } catch (error) {
        console.error('Failed to update user history:', error);
        if (error instanceof ApiError) {
          alert(`Failed to update user history: ${error.message}`);
        } else {
          alert('Failed to update user history. Please try again.');
        }
      }
    },
    [sessionForm, selectedPackageId, selectedPatientId, selectedAppointmentId, uploadFile]
  );

  // Calculate section states for indicators
  const totalSections = formSchema?.data.total_sections || 5;
  const sectionStates = React.useMemo(() => {
    const states: { [key: number]: 'active' | 'completed' | 'upcoming' } = {};
    for (let i = 1; i <= totalSections; i++) {
      if (completedSections.has(i)) {
        states[i] = 'completed';
      } else if (i === currentSection) {
        states[i] = 'active';
      } else {
        states[i] = 'upcoming';
      }
    }
    return states;
  }, [currentSection, completedSections, totalSections]);

  const toggleSection = useCallback((sectionNum: number) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionNum)) {
        newSet.delete(sectionNum);
      } else {
        newSet.add(sectionNum);
        setCurrentSection(sectionNum);
      }
      return newSet;
    });
  }, []);

  const handleSectionClick = useCallback((sectionNum: number) => {
    setCurrentSection(sectionNum);
    setExpandedSections((prev) => new Set([...prev, sectionNum]));
    // Scroll to section
    setTimeout(() => {
      const section = document.querySelector(`[data-section="${sectionNum}"]`);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setShowErrorBanner(false);
      setTesterNameError(null);

      // Validate tester name first
      const testerError = validateTesterName(formData.tester_name);
      if (testerError) {
        setTesterNameError(testerError);
        setShowErrorBanner(true);
        setTimeout(() => {
          testerNameRef.current?.focus();
          testerNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }

      // Validate entire form
      if (!validate()) {
        setShowErrorBanner(true);
        // Scroll to first error
        const firstError = errors[0];
        if (firstError) {
          const field = document.querySelector(`[name="${firstError.field}"]`);
          if (field) {
            field.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (field as HTMLElement).focus();
          }
        }
        return;
      }

      setIsSubmitting(true);
      try {
        const result = await analyzeCase({
          ...formData,
          session_id: session.sessionId,
          completion_time_seconds: session.getCompletionTime(),
          patient_id: selectedPatientId !== '' ? selectedPatientId : undefined,
        } as any);
        
        setAnalysisResult(result);
        setShowOutput(true);

        // Save form response to database (non-blocking)
        try {
          // Extract primary and secondary root causes
          const primaryRootCause = result.root_causes && result.root_causes.length > 0
            ? result.root_causes[0].simple_term
            : undefined;
          
          const secondaryRootCause = result.root_causes && result.root_causes.length > 1
            ? result.root_causes[1].simple_term
            : undefined;

          const saveResponse = await saveFormResponse({
            tester_email: email || formData.tester_name,
            patient_id: selectedPatientId !== '' ? parseInt(String(selectedPatientId)) : undefined,
            appointment_id: selectedAppointmentId ? selectedAppointmentId : undefined,
            form_data: {
              ...formData,
              session_id: session.sessionId,
              completion_time_seconds: session.getCompletionTime(),
              patient_id: selectedPatientId !== '' ? selectedPatientId : undefined,
            },
            analysis_result: result,
            session_id: session.sessionId,
            completion_time_seconds: session.getCompletionTime(),
            primary_root_cause: primaryRootCause,
            secondary_root_cause: secondaryRootCause,
            root_cause_explanation: result.root_cause_explanation || undefined,
            other_information: formData.additional_info || undefined,
          });
          
          console.log('Form response saved successfully:', saveResponse);
        } catch (saveError) {
          // Log error but don't block user experience
          console.error('Failed to save form response:', saveError);
          if (saveError instanceof ApiError) {
            console.error('Save error details:', saveError.message);
          }
        }
      } catch (error: any) {
        // Handle ApiError with better error messages
        if (error instanceof ApiError) {
          let errorMessage = error.message;
          
          // Provide user-friendly messages for specific error types
          if (error.isCorsError && error.isCorsError()) {
            errorMessage = 'CORS Error: The server is not configured to accept requests from this origin. Please contact the administrator or check server CORS settings.\n\n' +
              'This is a server-side configuration issue. The backend at http://localhost:8000 needs to allow requests from http://localhost:3005.';
          } else if (error.isTimeout()) {
            errorMessage = 'Request timed out. The analysis is taking longer than expected. Please try again.';
          } else if (error.isNetworkError()) {
            errorMessage = 'Network error. Please check your internet connection and try again.\n\n' +
              'If you see "CORS" or "Access-Control" in the browser console, this is a server configuration issue.';
          } else if (error.isServerError()) {
            errorMessage = 'Server error. Please try again later.';
          } else if (error.isClientError()) {
            errorMessage = error.message || 'Invalid request. Please check your form data.';
          }
          
          alert(`Error: ${errorMessage}`);
        } else if (error instanceof Error) {
          // Check for CORS errors in generic errors
          const errorMsg = error.message || '';
          if (errorMsg.includes('CORS') || errorMsg.includes('Access-Control') || errorMsg.includes('Failed to fetch')) {
            alert('CORS Error: The server is not configured to accept requests from this origin.\n\n' +
              'Please check the backend CORS configuration at http://localhost:8000');
          } else {
            alert(`Error: ${errorMsg || 'Failed to analyze case. Please try again.'}`);
          }
        } else {
          alert('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validate, errors, session, setIsSubmitting, email, selectedPatientId, selectedAppointmentId]
  );

  const handleFieldClick = useCallback((fieldName: string) => {
    const field = document.querySelector(`[name="${fieldName}"]`);
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (field as HTMLElement).focus();
    }
  }, []);

  // Validate tester name field
  const validateTesterName = useCallback((value: string): string | null => {
    if (!value || value.trim() === '') {
      return 'Tester name is not filled';
    }
    if (value.trim().length < 2) {
      return 'Tester name must be at least 2 characters';
    }
    if (value.length > 100) {
      return 'Tester name must be less than 100 characters';
    }
    return null;
  }, []);

  // Convert errors array to object for easier access
  const errorMap = React.useMemo(() => {
    const map: { [key: string]: string } = {};
    errors.forEach((err) => {
      map[err.field] = err.message;
    });
    return map;
  }, [errors]);

  // Show loading state while schema is being fetched
  if (isLoadingSchema) {
    return (
      <div className="discovery-call-form">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  // Show error if schema failed to load
  if (!formSchema || !formSchema.data) {
    return (
      <div className="discovery-call-form">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p style={{ color: '#ff0000' }}>Failed to load form. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="discovery-call-form">
      <ProgressHeader
        currentSection={currentSection}
        totalSections={totalSections}
        onSectionClick={handleSectionClick}
        sectionStates={sectionStates}
      />

      {showErrorBanner && (
        <ErrorBanner
          errors={errors}
          testerNameError={testerNameError || undefined}
          onClose={() => setShowErrorBanner(false)}
          onFieldClick={handleFieldClick}
        />
      )}

      <main className="main-container">
        <div className="form-panel">
          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <TesterNameField
              value={email || formData.tester_name || ''}
              onChange={(v) => updateField('tester_name', v)}
              onBlur={() => {
                const testerName = email || formData.tester_name || '';
                const error = validateTesterName(testerName);
                if (error) setTesterNameError(error);
                else setTesterNameError(null);
              }}
              error={testerNameError || errorMap.tester_name}
              disabled={true}
            />

            {/* Language Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}>
              <span style={{ fontWeight: 600, color: '#495057' }}>Language:</span>
              <div style={{
                display: 'flex',
                gap: '8px',
              }}>
                <button
                  type="button"
                  onClick={() => setLanguage('english')}
                  disabled={isLoadingSchema}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: language === 'english' ? '2px solid #007bff' : '1px solid #ced4da',
                    backgroundColor: language === 'english' ? '#007bff' : '#fff',
                    color: language === 'english' ? '#fff' : '#495057',
                    fontWeight: language === 'english' ? 600 : 400,
                    cursor: isLoadingSchema ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  🇬🇧 English
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('hindi')}
                  disabled={isLoadingSchema}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: language === 'hindi' ? '2px solid #007bff' : '1px solid #ced4da',
                    backgroundColor: language === 'hindi' ? '#007bff' : '#fff',
                    color: language === 'hindi' ? '#fff' : '#495057',
                    fontWeight: language === 'hindi' ? 600 : 400,
                    cursor: isLoadingSchema ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  🇮🇳 हिंदी
                </button>
              </div>
              {isLoadingSchema && (
                <span style={{ fontSize: '14px', color: '#6c757d' }}>Loading...</span>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <SelectField
                label="Select Appointment"
                name="appointment"
                value={selectedPatientId}
                onChange={(value) => {
                  const patientId = value === '' ? '' : Number(value);
                  setSelectedPatientId(patientId);
                  // Find and store the appointment ID
                  const selectedAppointment = appointments.find(
                    (apt) => apt.patient.id === Number(patientId)
                  );
                  setSelectedAppointmentId(selectedAppointment?.id || null);
                  setIsAppointmentApplied(false); // Reset applied state when selection changes
                }}
                placeholder="Select appointment"
                options={appointments.map((appointment) => ({
                  value: appointment.patient.id,
                  label: `(ID: ${appointment.patient.id},Mobile:${appointment.patient.mobile})`,
                }))}
                isLoading={isLoadingAppointments}
                required={false}
                disabled={isAppointmentApplied} // Disable after Apply is clicked
              />
              
              {selectedPatientId !== '' && !isAppointmentApplied && (
                <button
                  type="button"
                  onClick={handleApplyAppointment}
                  style={{
                    marginTop: '12px',
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: '200px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0056b3';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#007bff';
                  }}
                >
                  Apply
                </button>
              )}
            </div>

            {/* Show all sections only after appointment is applied */}
            {isAppointmentApplied && formSchema.data.sections.map((section) => (
              <FormSection
                key={section.section_number}
                sectionNumber={section.section_number}
                title={`Section ${section.section_number}: ${section.section_name}`}
                isExpanded={expandedSections.has(section.section_number)}
                isComplete={completedSections.has(section.section_number)}
                onToggle={() => toggleSection(section.section_number)}
              >
                {section.questions.map((question) => {
                  // Get field value safely with type assertion
                  const fieldValue = (formData as Record<string, any>)[question.field_name];
                  
                  return (
                    <DynamicFormField
                      key={question.id}
                      question={question}
                      value={fieldValue}
                      onChange={(value) => {
                        // Handle height field specially
                        if (question.type === 'height' && typeof value === 'object' && value !== null) {
                          const heightData = value as { cm?: number; feet?: number; inches?: number };
                          if (heightData.cm !== undefined) {
                            updateField('height_cm', heightData.cm);
                            updateField('height_feet', undefined);
                            updateField('height_inches', undefined);
                          } else {
                            updateField('height_cm', undefined);
                            updateField('height_feet', heightData.feet);
                            updateField('height_inches', heightData.inches);
                          }
                        } else {
                          // Use type assertion for dynamic field names
                          updateField(question.field_name as keyof FormData, value);
                        }
                      }}
                      onValidate={(fieldName, val, isRequired) => {
                        if (validateField) {
                          return validateField(fieldName as keyof FormData, val, isRequired);
                        }
                        return null;
                      }}
                      error={errorMap[question.field_name]}
                      disabled={!isFormEnabled}
                      formData={formData}
                      onNavigateToSection={handleSectionClick}
                    />
                  );
                })}
              </FormSection>
            ))}

            {isAppointmentApplied && (
              <div className="form-actions">
                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting || !isFormEnabled}
                >
                  {isSubmitting ? 'Analyzing...' : 'Submit for Analysis'}
                </button>
              </div>
            )}
          </form>
        </div>

        {showOutput && analysisResult && (
          <div className="analysis-output-panel">
            <div className="analysis-header">
              <div className="analysis-header-left">
                <h2 className="analysis-title">AI Analysis</h2>
                <span className="analysis-badge">Analysis Complete</span>
              </div>
              <button
                type="button"
                className="start-session-button"
                onClick={() => setShowStartSessionModal(true)}
              >
                Start New Session
              </button>
            </div>
            
            <div className="analysis-content">
              {/* PRIMARY ROOT CAUSE - First root cause or highest confidence */}
              {analysisResult.root_causes && analysisResult.root_causes.length > 0 && (
                <div className="root-cause-section">
                  <div className="root-cause-label">PRIMARY ROOT CAUSE</div>
                  <div className="root-cause-content">
                    {analysisResult.root_causes[0].simple_term}
                    {analysisResult.root_causes[0].category && analysisResult.root_causes[0].category !== analysisResult.root_causes[0].simple_term && (
                      <span className="root-cause-category">
                        {' '}[{analysisResult.root_causes[0].category}]
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* SECONDARY ROOT CAUSE - Second root cause */}
              {analysisResult.root_causes && analysisResult.root_causes.length > 1 && (
                <div className="root-cause-section">
                  <div className="root-cause-label">SECONDARY ROOT CAUSE</div>
                  <div className="root-cause-content">
                    {analysisResult.root_causes[1].simple_term}
                    {analysisResult.root_causes[1].category && analysisResult.root_causes[1].category !== analysisResult.root_causes[1].simple_term && (
                      <span className="root-cause-category">
                        {' '}[{analysisResult.root_causes[1].category}]
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Separator */}
              <div className="analysis-separator">==================</div>

              {/* ROOT CAUSE EXPLANATION */}
              {analysisResult.root_cause_explanation && (
                <div className="explanation-section">
                  <div className="explanation-label">ROOT CAUSE EXPLANATION</div>
                  <div className="explanation-content">
                    {analysisResult.root_cause_explanation.split('\n\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Separator */}
              {analysisResult.treatment_explanation && (
                <>
                  <div className="analysis-separator">==================</div>
                  
                  {/* TREATMENT EXPLANATION */}
                  <div className="explanation-section">
                    <div className="explanation-label">TREATMENT EXPLANATION</div>
                    <div className="explanation-content">
                      {analysisResult.treatment_explanation.split('\n\n').map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
      
      {showStartSessionModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Start New Session</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowStartSessionModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="modal-body" onSubmit={handleStartSessionSubmit}>
              <div className="modal-grid">
                <label className="modal-field">
                  <span>Treatment Package</span>
                  <select
                    value={selectedPackageId === '' ? '' : selectedPackageId}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '' : Number(e.target.value);
                      setSelectedPackageId(value);
                    }}
                  >
                    <option value="">Same as User History</option>
                    {isLoadingPackages && <option>Loading packages...</option>}
                    {!isLoadingPackages && packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {formatPackageLabel(pkg)}
                      </option>
                    ))}
                  </select>
                  {packageError && <small className="modal-error">{packageError}</small>}
                </label>

                <label className="modal-field">
                  <span>Treatment Recommendation</span>
                  <input
                    type="text"
                    value={sessionForm.treatmentRecommendation}
                    onChange={(e) => updateSessionField('treatmentRecommendation', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>

                <label className="modal-field">
                  <span>Type of sexologist recommended</span>
                  <select
                    value={sessionForm.sexologistType}
                    onChange={(e) => updateSessionField('sexologistType', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Psychiatrist">Psychiatrist</option>
                    <option value="Andrologist">Andrologist</option>
                    <option value="Gynaecologist">Gynaecologist</option>
                    <option value="Only Therapist">Only Therapist</option>
                    <option value="Venereologist">Venereologist</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Reason of treatment not started</span>
                  <select
                    value={sessionForm.reasonNotStarted}
                    onChange={(e) => updateSessionField('reasonNotStarted', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Payment done">Payment done</option>
                    <option value="Not sure of the reason">Not sure of the reason</option>
                    <option value="Treatment started in DC">Treatment started in DC</option>
                    <option value="Active pain / bleeding case / creep">Active pain / bleeding case / creep</option>
                    <option value="Treatment wanted in 99">Treatment wanted in 99</option>
                    <option value="Under age">Under age</option>
                    <option value="Low cibil score">Low cibil score</option>
                    <option value="Low self confidence because of low performance">Low self confidence because of low performance</option>
                    <option value="User found treatment costly">User found treatment costly</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Status of Lead</span>
                  <select
                    value={sessionForm.leadStatus}
                    onChange={(e) => updateSessionField('leadStatus', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Promise to Pay">Promise to Pay</option>
                    <option value="Feedback">Feedback</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Treatment Started in DC">Treatment Started in DC</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Discount Offered (If Any)</span>
                  <input
                    type="text"
                    value={sessionForm.discountOffered}
                    onChange={(e) => updateSessionField('discountOffered', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>

                <label className="modal-field">
                  <span>Add a Trigger of Treatment</span>
                  <select
                    value={sessionForm.triggerOfTreatment}
                    onChange={(e) => updateSessionField('triggerOfTreatment', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Low self confidence because of low performance">Low self confidence because of low performance</option>
                    <option value="Performance anxiety">Performance anxiety</option>
                    <option value="Partner feedback">Partner feedback</option>
                    <option value="Relationship issues">Relationship issues</option>
                    <option value="Health concerns">Health concerns</option>
                    <option value="Desire for improvement">Desire for improvement</option>
                    <option value="Medical recommendation">Medical recommendation</option>
                    <option value="Personal dissatisfaction">Personal dissatisfaction</option>
                  </select>
                </label>

                <label className="modal-field modal-field-full">
                  <span>Comments</span>
                  <textarea
                    value={sessionForm.comments}
                    onChange={(e) => updateSessionField('comments', e.target.value)}
                    placeholder="Same as User History"
                    rows={3}
                  />
                </label>

                <label className="modal-field">
                  <span>Upload File</span>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                  {uploadFile && <small className="modal-hint">{uploadFile.name}</small>}
                </label>

                <label className="modal-field">
                  <span>Lock-In</span>
                  <select
                    value={sessionForm.lockIn}
                    onChange={(e) => updateSessionField('lockIn', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Follow Up Date</span>
                  <input
                    type="date"
                    value={sessionForm.followUpDate}
                    onChange={(e) => updateSessionField('followUpDate', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>

                <label className="modal-field">
                  <span>Follow Up Time</span>
                  <input
                    type="time"
                    value={sessionForm.followUpTime}
                    onChange={(e) => updateSessionField('followUpTime', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowStartSessionModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => window.location.reload()}
                >
                  Skip
                </button>
                <button type="submit" className="primary-button">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscoveryCallForm;

